/* eslint react/prop-types: 0 */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-use-before-define */

import React from 'react';
import * as Yup from 'yup';
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
  useFetcher,
} from 'react-router-dom';
import useFormikTemplate from '../../Forms/hooks/useFormikTemplate';
import FormWrapper from '../../Forms/builders/FormWrapper';
import CreateForm from '../../Forms/CreateForm';
import { clear, add } from './AddProductAux';
import { clearFields, addFields, setAmountAndTax } from './formikFns';
import LBOffCanvas from '../../UI/LBOffCanvas';
import LinkWithQuery from './LinkWithQuery.tsx';

function AddProduct() {
  const { itemToChange: item, addItemHandler } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('productId') || null;

  const productsFetcher = useFetcher();

  React.useEffect(() => {
    if (!productsFetcher.data && productsFetcher.state === 'idle') {
      productsFetcher.load('/products');
    }
  }, [productsFetcher]);

  const productData =
    productId && productsFetcher?.data?.products?.length > 0
      ? productsFetcher.data?.products?.filter((p) => p.id === +productId)[0]
      : null;

  const [selectedCategory, setSelectedCategory] = React.useState(
    item?.category_id || productData?.category?.id || ''
  );

  React.useEffect(() => {
    if (!selectedCategory && productId) {
      setSelectedCategory(productData?.category?.id);
      handleProductSelection(productData);
    }
  }, [productData]);

  const handleAmountChange = async (event, label) => {
    if (Number.isNaN(+event.target.value)) {
      formik.setFieldError(label, 'Must be a number');
      await formik.setFieldValue(label, 0);
      return;
    }

    const { value } = event.target;
    const values = { ...formik.values };
    values[label] = Number(value);
    setAmountAndTax(values, formik.setFieldValue);
  };

  const handleCategorySelection = (category) => {
    if (!category) return;
    setSelectedCategory(category.family_id);
    addFields(add.category, formik.setFieldValue, category);
    clearFields(clear.category, formik.setFieldValue);
    formik.setFieldValue('quantity', '1');
  };

  const handleProductSelection = (product) => {
    if (!product?.category?.id)
      clearFields(clear.productClear, formik.setFieldValue);
    else {
      clearFields(clear.productChange, formik.setFieldValue);
      formik.setFieldValue('quantity', '1');
      setAmountAndTax(
        { ...formik.values, price_before_tax: product.amount.price_before_tax },
        formik.setFieldValue
      );
      addFields(add.product, formik.setFieldValue, product);
    }
  };

  const fields = [
    {
      name: 'category_id',
      label: 'Category',
      formType: 'productCategoryCombo',
      initialValue: item?.category_id || productData?.category?.id || '',
      type: Yup.string().required('Please complete the field'),
      className: item ? 'd-none' : '',
      required: true,
      change: (e) => handleCategorySelection(e),
    },
    {
      name: 'product_id',
      label: 'Product or service',
      initialValue: item?.product_id || productData?.id || '',
      type: Yup.string().required('Please complete the field'),
      required: true,
      formType: 'productCombo',
      categoryId: selectedCategory,
      className: item ? 'd-none' : '',
      change: (e) => handleProductSelection(e),
    },
    {
      name: 'product_name',
      initialValue: item?.product_name || productData?.name || '',
      type: Yup.string(),
      disabled: true,
      className: 'd-none',
    },
    {
      name: 'category_name',
      initialValue: item?.category_name || productData?.category?.name || '',
      type: Yup.string(),
      disabled: true,
      className: 'd-none',
    },
    {
      name: 'description',
      label: 'Description',
      initialValue: item?.description || productData?.description || '',
      type: Yup.string(),
      formType: 'textarea',
    },
    {
      formGroup: 'Values',
      groupChildren: [
        {
          name: 'quantity',
          label: 'Quantity',
          initialValue: item?.quantity || '1',
          type: Yup.number(),
          formType: 'number',
          change: (e) => handleAmountChange(e, 'quantity'),
          className: 'flex__span-6',
          inputMode: 'numeric',
        },
        {
          name: 'price_before_tax',
          label: 'Rate | Price',
          initialValue:
            item?.price_before_tax ||
            productData?.amount?.price_before_tax ||
            '',
          type: Yup.number(),
          inputMode: 'numeric',
          className: 'flex__span-6',
          change: (e) => handleAmountChange(e, 'price_before_tax'),
        },
        {
          name: 'item_price_before_tax',
          label: 'Total',
          initialValue:
            item?.item_price_before_tax ||
            productData?.amount?.price_before_tax ||
            '',
          type: Yup.number(),
          formType: 'number',
          disabled: true,
          readOnly: true,
          plaintext: true,
          className: 'flex__span-6',
          change: (e) => handleAmountChange(e, 'item_price_before_tax'),
        },
        {
          name: 'tax_rate',
          label: 'Tax',
          initialValue: item?.tax_rate || productData?.amount?.tax_rate || '',
          type: Yup.number(),
          formType: 'number',
          disabled: true,
          readOnly: true,
          plaintext: true,
          className: 'flex__span-6',
        },
        {
          name: 'item_id',
          label: 'item_id',
          initialValue: item?.item_id || '',
          type: Yup.string(),
          formType: 'number',
          disabled: true,
          readOnly: true,
          plaintext: true,
          className: 'd-none',
        },
      ],
    },
  ];

  const submitAction = () => {
    const nextItem = {
      ...formik.values,
      item_id: item?.item_id || Math.floor(Math.random() * 1001),
    };
    if (item?.index || item?.index === 0) nextItem.index = item.index;
    addItemHandler(nextItem);
    navigate(`..${location?.search}`);
    searchParams.delete('productId');
    setSearchParams(searchParams);
  };

  const [formik] = useFormikTemplate({
    initial: fields,
    yupValues: fields,
    submitAction,
    enableReinitialize: true,
  });

  return (
    <LBOffCanvas.Wrapper>
      <LBOffCanvas.Left>
        <LBOffCanvas.Header>
          <div className="d-flex flex-column gap-1">
            {item ? `Update ${item?.product_name || 'product'}` : 'Add product'}
            <LinkWithQuery
              to="../create-product"
              style={{ fontSize: '0.8rem' }}
            >
              Create new product
            </LinkWithQuery>
          </div>
        </LBOffCanvas.Header>
        <FormWrapper
          className="flex__form-wrapper"
          handleSubmit={formik.handleSubmit}
          id="add-product-form"
          portalId="offcanvas-action-group-left"
          isSubmitting={formik.isSubmitting}
          dirty={formik.dirty}
          isValid
          label={item ? `Update ${item.product_name}` : 'Add'}
        >
          <CreateForm structure={fields} {...formik} />
        </FormWrapper>
      </LBOffCanvas.Left>
    </LBOffCanvas.Wrapper>
  );
}

export default AddProduct;
